
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, ComposedChart
} from "recharts";
import { getEmailLogs } from '@/utils/emailUtils';
import { genericSupabase } from '@/integrations/supabase/client';
import { useLeads } from '@/context/LeadContext';
import { usePartners } from '@/context/PartnerContext';
import { differenceInHours, format, parseISO, subDays } from 'date-fns';
import {
  Mail,
  Eye,
  Clock,
  BarChart2,
  MousePointer,
  User,
  Calendar,
  ArrowDownToLine,
  ArrowRight,
  Trash2,
  AlertCircle
} from 'lucide-react';
import StatisticsCard from '@/components/dashboard/StatisticsCard';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PartnerStatisticsCard from '@/components/partners/PartnerStatisticsCard';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

interface EmailLog {
  id: string;
  trackingid: string;
  leadid: string;
  partnerid: string | null;
  email: string | null;
  event: string;
  timestamp: string;
  created_at: string;
  useragent: string | null;
  ip: string | null;
  lead?: {
    name: string;
    service: string;
  };
  partner?: {
    name: string;
  };
}

const EmailTrackingStats = () => {
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { leads } = useLeads();
  const { partners } = usePartners();
  const { toast } = useToast();

  const weekAgo = subDays(new Date(), 7);

  useEffect(() => {
    fetchEmailLogs();
  }, [leads, partners]);

  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      let logs: EmailLog[] = [];
      try {
        const apiLogs = await getEmailLogs();
        if (apiLogs && apiLogs.length > 0) {
          logs = apiLogs.map((log: any) => ({
            ...log,
            id: log.id || `api-${Date.now()}-${Math.random()}`,
            leadid: log.leadId,
            partnerid: log.partnerId,
            trackingid: log.trackingId,
            timestamp: log.timestamp,
            created_at: log.timestamp,
            useragent: log.userAgent,
          }));
        }
      } catch (apiError) {
        console.warn('Error fetching from API, falling back to Supabase:', apiError);

        const { data, error } = await genericSupabase
          .from('email_logs')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        logs = data || [];
      }

      const enrichedLogs = logs.map(log => {
        const relatedLead = leads.find(lead => lead.id === log.leadid);
        const relatedPartner = partners.find(partner => partner.id === log.partnerid);

        return {
          ...log,
          lead: relatedLead ? {
            name: relatedLead.name,
            service: relatedLead.service
          } : undefined,
          partner: relatedPartner ? {
            name: relatedPartner.name
          } : undefined
        };
      });

      setEmailLogs(enrichedLogs);

      if (enrichedLogs.length === 0) {
        setError('No email logs found.');
      }
    } catch (fetchError) {
      console.error('Error fetching email logs:', fetchError);
      setError('Failed to load email statistics.');
    } finally {
      setLoading(false);
    }
  };

  const deleteEmailLog = async (id: string) => {
    try {
      if (id.startsWith('api-')) {
        setEmailLogs(prevLogs => prevLogs.filter(log => log.id !== id));
        toast({
          title: "Επιτυχής διαγραφή",
          description: "Η καταγραφή αφαιρέθηκε από την προβολή.",
        });
        return;
      }

      const { error } = await genericSupabase
        .from('email_logs')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setEmailLogs(prevLogs => prevLogs.filter(log => log.id !== id));

      toast({
        title: "Επιτυχής διαγραφή",
        description: "Η καταγραφή διαγράφηκε επιτυχώς.",
      });
    } catch (error) {
      console.error('Error deleting email log:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η διαγραφή της καταγραφής. Προσπαθήστε ξανά.",
      });
    }
  };

  const filteredLogs = emailLogs.filter(log => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (log.email && log.email.toLowerCase().includes(searchLower)) ||
      (log.leadid && log.leadid.toLowerCase().includes(searchLower)) ||
      (log.lead?.name && log.lead.name.toLowerCase().includes(searchLower)) ||
      (log.partner?.name && log.partner.name.toLowerCase().includes(searchLower))
    );
  });

  const groupedByTracking = React.useMemo(() => {
    const grouped: Record<string, EmailLog[]> = {};

    filteredLogs.forEach(log => {
      if (!grouped[log.trackingid]) {
        grouped[log.trackingid] = [];
      }
      grouped[log.trackingid].push(log);
    });

    Object.values(grouped).forEach(group => {
      group.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    });

    return grouped;
  }, [filteredLogs]);

  const stats = React.useMemo(() => {
    const sentEmails = filteredLogs.filter(log => log.event === 'sent').length;
    const openedEmails = Object.values(groupedByTracking).filter(group =>
      group.some(log => log.event === 'open')
    ).length;

    const clickedEmails = Object.values(groupedByTracking).filter(group =>
      group.some(log => log.event.startsWith('click'))
    ).length;

    const openRate = sentEmails > 0 ? (openedEmails / sentEmails) * 100 : 0;

    const clickRate = openedEmails > 0 ? (clickedEmails / openedEmails) * 100 : 0;

    let totalOpenTime = 0;
    let openedCount = 0;

    Object.values(groupedByTracking).forEach(group => {
      const sentLog = group.find(log => log.event === 'sent');
      const openLog = group.find(log => log.event === 'open');

      if (sentLog && openLog) {
        const sentTime = new Date(sentLog.timestamp).getTime();
        const openTime = new Date(openLog.timestamp).getTime();
        const hoursDiff = (openTime - sentTime) / (1000 * 60 * 60);

        if (hoursDiff >= 0 && hoursDiff < 168) {
          totalOpenTime += hoursDiff;
          openedCount++;
        }
      }
    });

    const avgTimeToOpen = openedCount > 0 ? (totalOpenTime / openedCount) : 0;

    const recentEmails = filteredLogs.filter(log =>
      log.event === 'sent' && new Date(log.timestamp) >= weekAgo
    ).length;

    return {
      sent: sentEmails,
      opened: openedEmails,
      clicked: clickedEmails,
      openRate,
      clickRate,
      avgTimeToOpen,
      recentEmails
    };
  }, [filteredLogs, groupedByTracking, weekAgo]);

  const emailChartData = React.useMemo(() => {
    const dateGroups: Record<string, { date: string, sent: number, opened: number, clicked: number }> = {};

    filteredLogs.forEach(log => {
      const dateStr = format(new Date(log.timestamp), 'yyyy-MM-dd');

      if (!dateGroups[dateStr]) {
        dateGroups[dateStr] = {
          date: format(new Date(log.timestamp), 'd MMM'),
          sent: 0,
          opened: 0,
          clicked: 0
        };
      }

      if (log.event === 'sent') {
        dateGroups[dateStr].sent++;
      } else if (log.event === 'open') {
        dateGroups[dateStr].opened++;
      } else if (log.event.startsWith('click')) {
        dateGroups[dateStr].clicked++;
      }
    });

    return Object.values(dateGroups)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [filteredLogs]);

  const clickTypeDistribution = React.useMemo(() => {
    const clickTypes: Record<string, number> = {};

    filteredLogs
      .filter(log => log.event.startsWith('click-'))
      .forEach(log => {
        const clickType = log.event.replace('click-', '');
        clickTypes[clickType] = (clickTypes[clickType] || 0) + 1;
      });

    const clickTypeLabels: Record<string, string> = {
      'view': 'Προβολή',
      'status-pending': 'Αναλήφθηκε',
      'status-completed': 'Ολοκληρώθηκε',
      'status-info': 'Πληροφορίες'
    };

    const clickTypeColors: Record<string, string> = {
      'view': '#3b82f6',
      'status-pending': '#f59e0b',
      'status-completed': '#10b981',
      'status-info': '#6366f1'
    };

    return Object.entries(clickTypes).map(([type, count]) => ({
      name: clickTypeLabels[type] || type,
      value: count,
      color: clickTypeColors[type] || '#888888'
    }));
  }, [filteredLogs]);

  const partnerEmailStats = React.useMemo(() => {
    const partnerStats: Record<string, {
      partnerId: string;
      partnerName: string;
      sent: number;
      opened: number;
      clicked: number;
      openRate: number;
    }> = {};

    filteredLogs
      .filter(log => log.partnerid && log.event === 'sent')
      .forEach(log => {
        const partnerId = log.partnerid as string;
        const partner = partners.find(p => p.id === partnerId);
        const partnerName = partner?.name || log.partner?.name || `Partner ${partnerId.substring(0, 8)}`;

        if (!partnerStats[partnerId]) {
          partnerStats[partnerId] = {
            partnerId,
            partnerName,
            sent: 0,
            opened: 0,
            clicked: 0,
            openRate: 0
          };
        }

        partnerStats[partnerId].sent++;
      });

    Object.values(groupedByTracking).forEach(group => {
      const partnerLog = group.find(log => log.partnerid);
      if (!partnerLog || !partnerLog.partnerid) return;

      const partnerId = partnerLog.partnerid;
      if (!partnerStats[partnerId]) {
        const partner = partners.find(p => p.id === partnerId);
        const partnerName = partner?.name || `Partner ${partnerId.substring(0, 8)}`;

        partnerStats[partnerId] = {
          partnerId,
          partnerName,
          sent: 0,
          opened: 0,
          clicked: 0,
          openRate: 0
        };
      }

      if (group.some(log => log.event === 'open')) {
        partnerStats[partnerId].opened++;
      }

      if (group.some(log => log.event.startsWith('click'))) {
        partnerStats[partnerId].clicked++;
      }
    });

    Object.values(partnerStats).forEach(stats => {
      stats.openRate = stats.sent > 0 ? (stats.opened / stats.sent) * 100 : 0;
    });

    return Object.values(partnerStats)
      .sort((a, b) => b.sent - a.sent)
      .slice(0, 10);
  }, [filteredLogs, groupedByTracking, partners]);

  const emailEvents = React.useMemo(() => {
    return Object.entries(groupedByTracking).map(([trackingId, logs]) => {
      const sentLog = logs.find(log => log.event === 'sent');
      const openLog = logs.find(log => log.event === 'open');
      const clickLogs = logs.filter(log => log.event.startsWith('click'));

      const lead = logs[0]?.lead;
      const partner = logs[0]?.partner;
      const email = logs[0]?.email;

      let openTime = null;
      if (sentLog && openLog) {
        const sentDate = new Date(sentLog.timestamp);
        const openDate = new Date(openLog.timestamp);
        const hoursDiff = differenceInHours(openDate, sentDate);
        openTime = hoursDiff;
      }

      return {
        trackingId,
        leadId: logs[0]?.leadid,
        partnerId: logs[0]?.partnerid,
        email,
        leadName: lead?.name,
        partnerName: partner?.name,
        sentTime: sentLog ? new Date(sentLog.timestamp) : null,
        openTime,
        wasOpened: !!openLog,
        clicks: clickLogs.length,
        clickTypes: clickLogs.map(log => log.event.replace('click-', '')),
        logIds: logs.map(log => log.id)
      };
    })
      .filter(event => event.sentTime)
      .sort((a, b) => {
        if (!a.sentTime || !b.sentTime) return 0;
        return b.sentTime.getTime() - a.sentTime.getTime();
      });
  }, [groupedByTracking]);

  const chartConfig = {
    sent: { label: "Απεσταλμένα", theme: { light: "#3b82f6", dark: "#60a5fa" } },
    opened: { label: "Αναγνωσμένα", theme: { light: "#10b981", dark: "#34d399" } },
    clicked: { label: "Κλικ", theme: { light: "#f59e0b", dark: "#fbbf24" } }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg">
            <Mail className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Παρακολούθηση Email</h1>
            <p className="text-slate-500 mt-0.5">Στατιστικά και ανάλυση αλληλεπιδράσεων email</p>
          </div>
        </div>

        <Button onClick={fetchEmailLogs} variant="outline" className="border-slate-200">
          <ArrowRight className="mr-2 h-4 w-4" /> Ανανέωση
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <div className="relative flex-1 max-w-md">
              <User className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Αναζήτηση με όνομα, email ή ID επαφής..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="ghost" onClick={() => setSearchTerm("")}>
              Καθαρισμός
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center p-12">
          <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      )}

      {error && !loading && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center p-6 text-center text-red-800">
              <Mail className="h-12 w-12 text-red-300 mb-4" />
              <h3 className="text-lg font-medium">{error}</h3>
              <p className="mt-2 text-red-600">
                Δεν υπάρχουν διαθέσιμα στατιστικά email αυτή τη στιγμή.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatisticsCard
              title="Απεσταλμένα Emails"
              value={stats.sent}
              icon={<Mail />}
              variant="info"
              description="Συνολικά απεσταλμένα emails"
            />

            <StatisticsCard
              title="Ποσοστό Αναγνώσεων"
              value={`${Math.round(stats.openRate)}%`}
              icon={<Eye />}
              variant={stats.openRate >= 50 ? "success" : "warning"}
              description="Ποσοστό emails που αναγνώστηκαν"
            />

            <StatisticsCard
              title="Ποσοστό Κλικ"
              value={`${Math.round(stats.clickRate)}%`}
              icon={<MousePointer />}
              variant={stats.clickRate >= 30 ? "success" : "warning"}
              description="Ποσοστό επί των αναγνωσμένων"
            />

            <StatisticsCard
              title="Μέσος Χρόνος Ανάγνωσης"
              value={`${Math.round(stats.avgTimeToOpen * 10) / 10} ώρες`}
              icon={<Clock />}
              variant={stats.avgTimeToOpen <= 4 ? "success" : stats.avgTimeToOpen <= 24 ? "warning" : "danger"}
              description="Από την αποστολή έως την ανάγνωση"
            />
          </div>

          <Tabs defaultValue="overview">
            <TabsList className="bg-slate-100 p-1 rounded-lg w-full max-w-[600px] grid grid-cols-3 mb-6">
              <TabsTrigger value="overview" className="rounded-md data-[state=active]:bg-white">Επισκόπηση</TabsTrigger>
              <TabsTrigger value="emails" className="rounded-md data-[state=active]:bg-white">Λίστα Emails</TabsTrigger>
              <TabsTrigger value="partners" className="rounded-md data-[state=active]:bg-white">Ανά Συνεργάτη</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-blue-50">
                        <BarChart2 className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Δραστηριότητα Emails</CardTitle>
                        <CardDescription>Στατιστικά αποστολής, ανάγνωσης και κλικ</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="h-[350px] w-full overflow-hidden">
                      <ChartContainer config={chartConfig}>
                        <ResponsiveContainer width="100%" height="100%" debounce={50}>
                          <BarChart data={emailChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Legend />
                            <Bar dataKey="sent" name="Απεσταλμένα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="opened" name="Αναγνωσεις" fill="#10b981" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="clicked" name="Κλικ" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-violet-50">
                        <MousePointer className="h-4 w-4 text-violet-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Κατανομή Κλικ</CardTitle>
                        <CardDescription>Είδος ενεργειών από τα κλικ στα emails</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {clickTypeDistribution.length > 0 ? (
                      <div className="h-[350px] w-full overflow-hidden">
                        <ResponsiveContainer width="100%" height="100%" debounce={50}>
                          <PieChart>
                            <Pie
                              data={clickTypeDistribution}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              paddingAngle={2}
                            >
                              {clickTypeDistribution.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => [`${value} κλικ`, 'Πλήθος']}
                              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[350px] p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-slate-300 mb-4" />
                        <h3 className="text-lg font-medium text-slate-900">Δεν έχουν καταγραφεί κλικ</h3>
                        <p className="text-slate-500 mt-2">
                          Δεν υπάρχουν καταγεγραμμένα κλικ σε emails προς το παρόν.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="emails" className="space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-emerald-50">
                      <Mail className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Λίστα Απεσταλμένων Emails</CardTitle>
                      <CardDescription>Λεπτομερή στατιστικά ανά email</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ημερομηνία</TableHead>
                        <TableHead className="w-[200px]">Παραλήπτης</TableHead>
                        <TableHead>Επαφή</TableHead>
                        <TableHead>Άνοιγμα</TableHead>
                        <TableHead>Κλικ</TableHead>
                        <TableHead>Ενέργεια</TableHead>
                        <TableHead className="text-right">Διαγραφή</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailEvents.length > 0 ? (
                        emailEvents.slice(0, 50).map((event, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                <span className="text-sm">
                                  {event.sentTime ?
                                    format(event.sentTime, 'dd/MM/yyyy HH:mm') :
                                    'Άγνωστο'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium truncate max-w-[180px] text-slate-900">{event.partnerName || 'Άγνωστος'}</div>
                              <div className="text-xs text-slate-500 truncate max-w-[180px]">{event.email || 'Email μη διαθέσιμο'}</div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-normal bg-slate-50 border-slate-200">
                                {event.leadName || event.leadId?.substring(0, 8) || 'Άγνωστη'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {event.wasOpened ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                                  <span className="text-sm text-emerald-700 font-medium">Ναι</span>
                                  {event.openTime !== null && (
                                    <span className="text-xs text-slate-400">({event.openTime}h)</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400">Όχι ακόμα</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {event.clicks > 0 ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200">{event.clicks} κλικ</Badge>
                              ) : (
                                <span className="text-sm text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {event.clickTypes.includes('status-completed') ? (
                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200">Ολοκληρώθηκε</Badge>
                              ) : event.clickTypes.includes('status-pending') ? (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200">Αναλήφθηκε</Badge>
                              ) : event.clickTypes.includes('view') ? (
                                <Badge variant="outline">Προβολή</Badge>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Διαγραφή εγγραφής</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την καταγραφή;
                                      Η ενέργεια αυτή είναι μη αναστρέψιμη.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Άκυρο</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-red-500 hover:bg-red-600 text-white"
                                      onClick={() => event.logIds.forEach(id => deleteEmailLog(id))}
                                    >
                                      Διαγραφή
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="h-32 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <Mail className="h-8 w-8 text-slate-300 mb-2" />
                              <p className="text-slate-500 font-medium">Δεν βρέθηκαν emails.</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </div>

                  {emailEvents.length > 50 && (
                    <div className="flex justify-center mt-6">
                      <Button variant="outline" className="border-slate-200 text-slate-600">
                        <ArrowDownToLine className="h-4 w-4 mr-2" />
                        Εμφάνιση περισσότερων
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="partners" className="space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-orange-50">
                      <User className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Επιδόσεις Συνεργατών</CardTitle>
                      <CardDescription>Στατιστικά αποστολής και ανάγνωσης emails ανά συνεργάτη</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {partnerEmailStats.length > 0 ? (
                    <>
                      <div className="h-[400px] w-full overflow-hidden">
                        <ResponsiveContainer width="100%" height="100%" debounce={50}>
                          <ComposedChart
                            data={partnerEmailStats}
                            margin={{ top: 5, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis
                              dataKey="partnerName"
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              interval={0}
                              tick={{ fontSize: 11 }}
                              stroke="#94a3b8"
                            />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="sent" name="Απεσταλμένα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="left" dataKey="opened" name="Αναγνωσμένα" fill="#10b981" radius={[4, 4, 0, 0]} />
                            <Bar yAxisId="left" dataKey="clicked" name="Με κλικ" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="openRate"
                              name="Ποσοστό ανάγνωσης (%)"
                              stroke="#ef4444"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="space-y-4 mt-8">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Λεπτομερης Αναλυση</h3>
                        <div className="rounded-md border border-slate-200 overflow-x-auto">
                          <Table>
                            <TableHeader className="bg-slate-50">
                              <TableRow>
                                <TableHead className="font-semibold text-slate-700">Συνεργάτης</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-right">Απεσταλμένα</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-right">Αναγνωσμένα</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-right">Κλικ</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-right">Ποσοστό (%)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {partnerEmailStats.map((stat, i) => (
                                <TableRow key={i} className="hover:bg-slate-50/50">
                                  <TableCell className="font-medium text-slate-900">{stat.partnerName}</TableCell>
                                  <TableCell className="text-right">{stat.sent}</TableCell>
                                  <TableCell className="text-right text-emerald-600 font-medium">{stat.opened}</TableCell>
                                  <TableCell className="text-right text-amber-600 font-medium">{stat.clicked}</TableCell>
                                  <TableCell className="text-right font-bold text-slate-900">{Math.round(stat.openRate)}%</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                      <User className="h-12 w-12 text-slate-300 mb-4" />
                      <h3 className="text-lg font-medium text-slate-900">Δεν υπάρχουν δεδομένα συνεργατών</h3>
                      <p className="text-slate-500 mt-2">
                        Δεν βρέθηκαν στατιστικά emails για τους συνεργάτες.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default EmailTrackingStats;
